import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { LuChevronDown, LuLoaderCircle, LuPlus, LuSearch } from "react-icons/lu";
import { cn } from "@/lib/utils";
import { SqButton } from "@/components/squircle-ui/button";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import {
  RiArrowGoBackLine,
  RiCheckLine,
  RiDeleteBinLine,
  RiEditLine,
  RiScissorsLine,
} from "@remixicon/react";
import {
  createUgcVideoEditingPrompt,
  deleteUgcVideoEditingPrompt,
  fetchUgcVideoEditingPrompts,
  updateUgcVideoEditingPrompt,
} from "@/screens/creative-library/apiService";
import { UgcVideoEditingPrompt } from "@/screens/creative-library/types";
import { Button } from "@/components/ui/button";

interface Props {
  accountId: string;
  value: string | null;
  onChange: (promptId: string | null) => void;
  disabled?: boolean;
  preload?: boolean;
}

const UgcVideoEditingPromptDropdown: React.FC<Props> = ({
  accountId,
  value,
  onChange,
  disabled = false,
  preload = false,
}) => {
  const [open, setOpen] = useState(false);

  const [recommendedPrompts, setRecommendedPrompts] = useState<UgcVideoEditingPrompt[]>(
    [],
  );
  const [createdPrompts, setCreatedPrompts] = useState<UgcVideoEditingPrompt[]>([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const hasFetchedPromptsRef = useRef(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [recommendedOpen, setRecommendedOpen] = useState(true);
  const [createdOpen, setCreatedOpen] = useState(true);

  const [isAddingNewPrompt, setIsAddingNewPrompt] = useState(false);
  const [newPromptText, setNewPromptText] = useState("");
  const [isCreatingPrompt, setIsCreatingPrompt] = useState(false);
  const newPromptInputRef = useRef<HTMLInputElement>(null);

  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [isUpdatingPrompt, setIsUpdatingPrompt] = useState(false);
  const editPromptInputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set());
  const deleteTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const originalSelectionsBeforeDeleteRef = useRef<Map<string, string | null>>(new Map());

  useEffect(() => {
    hasFetchedPromptsRef.current = false;
    setRecommendedPrompts([]);
    setCreatedPrompts([]);
    setSearchQuery("");
    setEditingPromptId(null);
    setIsAddingNewPrompt(false);
    setNewPromptText("");
    setPendingDeleteIds(new Set());
    deleteTimersRef.current.forEach((t) => clearTimeout(t));
    deleteTimersRef.current.clear();
    originalSelectionsBeforeDeleteRef.current.clear();
  }, [accountId]);

  const loadPrompts = async (): Promise<void> => {
    if (!accountId) return;
    if (disabled) return;
    if (hasFetchedPromptsRef.current) return;
    if (isLoadingPrompts) return;
    setIsLoadingPrompts(true);
    try {
      const res = await fetchUgcVideoEditingPrompts({ accountId });
      setRecommendedPrompts(res.recommended ?? []);
      setCreatedPrompts(res.created ?? []);
      hasFetchedPromptsRef.current = true;
    } catch (e) {
      console.error("Failed to fetch ugc video editing prompts:", e);
      toast.message(
        <ToastContent
          headingPrefix="Failed to load prompts"
          subheadingPrefix="Please try again."
          isError={true}
        />,
      );
    } finally {
      setIsLoadingPrompts(false);
    }
  };

  // Prefetch prompts (before dropdown is opened) when requested
  useEffect(() => {
    if (!preload) return;
    void loadPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preload, accountId, disabled]);

  // Fetch prompts when opening (fallback if not preloaded)
  useEffect(() => {
    if (!open) return;
    void loadPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus edit input when editing begins
  useEffect(() => {
    if (!editingPromptId) return;
    const t = setTimeout(() => {
      editPromptInputRef.current?.focus();
      editPromptInputRef.current?.select();
    }, 0);
    return () => clearTimeout(t);
  }, [editingPromptId]);

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = deleteTimersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const allPrompts = useMemo<UgcVideoEditingPrompt[]>(
    () => [...recommendedPrompts, ...createdPrompts],
    [recommendedPrompts, createdPrompts],
  );

  const selectedPrompt = useMemo<UgcVideoEditingPrompt | null>(() => {
    if (!value) return null;
    return allPrompts.find((p) => p.id === value) || null;
  }, [allPrompts, value]);

  const isLoadingSelected = !!value && !selectedPrompt && isLoadingPrompts;

  // Auto-select first prompt when prompts are loaded and none is selected
  // Auto-select first prompt when prompts are loaded and none is selected
  useEffect(() => {
    if (allPrompts.length > 0 && !value && !isLoadingPrompts) {
      const firstPrompt =
        recommendedPrompts.length > 0
          ? recommendedPrompts[0]
          : createdPrompts.length > 0
            ? createdPrompts[0]
            : null;
      if (firstPrompt) {
        onChange(firstPrompt.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPrompts.length, isLoadingPrompts]);

  const filteredRecommended = useMemo<UgcVideoEditingPrompt[]>(() => {
    if (!searchQuery.trim()) return recommendedPrompts;
    const q = searchQuery.toLowerCase();
    return recommendedPrompts.filter((p) => p.prompt.toLowerCase().includes(q));
  }, [recommendedPrompts, searchQuery]);

  const filteredCreated = useMemo<UgcVideoEditingPrompt[]>(() => {
    if (!searchQuery.trim()) return createdPrompts;
    const q = searchQuery.toLowerCase();
    return createdPrompts.filter((p) => p.prompt.toLowerCase().includes(q));
  }, [createdPrompts, searchQuery]);

  const handleSelectPrompt = (promptItem: UgcVideoEditingPrompt) => {
    onChange(promptItem.id);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearchQuery("");
      if (editingPromptId) setEditingPromptId(null);
      if (isAddingNewPrompt) {
        setIsAddingNewPrompt(false);
        setNewPromptText("");
      }
    }
  };

  const handleNewPrompt = () => {
    setIsAddingNewPrompt(true);
    setNewPromptText("");
    setCreatedOpen(true);
    setTimeout(() => newPromptInputRef.current?.focus(), 0);
  };

  const handleCreatePrompt = async (): Promise<void> => {
    const v = newPromptText.trim();
    if (!v || !accountId || isCreatingPrompt) return;
    setIsCreatingPrompt(true);
    try {
      const res = await createUgcVideoEditingPrompt({
        accountId,
        prompt: v,
      });
      setCreatedPrompts((prev) => [...prev, res.data]);
      setNewPromptText("");
      setIsAddingNewPrompt(false);
    } catch (e) {
      console.error("Failed to create ugc video editing prompt:", e);
      toast.message(
        <ToastContent
          headingPrefix="Failed to create prompt"
          subheadingPrefix="Please try again."
          isError={true}
        />,
      );
    } finally {
      setIsCreatingPrompt(false);
    }
  };

  const handleEditPrompt = (promptItem: UgcVideoEditingPrompt) => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setEditingPromptId(promptItem.id);
  };

  const handleCancelEdit = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setEditingPromptId(null);
  };

  const handleUpdatePrompt = async (): Promise<void> => {
    const v = editPromptInputRef.current?.value.trim() || "";
    if (!v || !accountId || !editingPromptId || isUpdatingPrompt) return;

    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    setIsUpdatingPrompt(true);
    const promptIdToUpdate = editingPromptId;

    let original: UgcVideoEditingPrompt | undefined;
    setCreatedPrompts((prev) => {
      original = prev.find((p) => p.id === promptIdToUpdate);
      return prev.map((p) => (p.id === promptIdToUpdate ? { ...p, prompt: v } : p));
    });
    setEditingPromptId(null);

    try {
      const res = await updateUgcVideoEditingPrompt({
        accountId,
        promptId: promptIdToUpdate,
        prompt: v,
      });
      setCreatedPrompts((prev) =>
        prev.map((p) => (p.id === promptIdToUpdate ? res.data : p)),
      );
    } catch (e) {
      console.error("Failed to update ugc video editing prompt:", e);
      toast.message(
        <ToastContent
          headingPrefix="Failed to update prompt"
          subheadingPrefix="Please try again."
          isError={true}
        />,
      );
      if (original) {
        setCreatedPrompts((prev) =>
          prev.map((p) => (p.id === promptIdToUpdate ? original! : p)),
        );
      }
    } finally {
      setIsUpdatingPrompt(false);
    }
  };

  const clearDeleteTimer = (promptId?: string) => {
    if (promptId) {
      const t = deleteTimersRef.current.get(promptId);
      if (t) {
        clearTimeout(t);
        deleteTimersRef.current.delete(promptId);
      }
      return;
    }
    deleteTimersRef.current.forEach((t) => clearTimeout(t));
    deleteTimersRef.current.clear();
  };

  const executeDelete = async (promptId: string): Promise<void> => {
    if (!accountId) return;
    try {
      await deleteUgcVideoEditingPrompt({ accountId, promptId });

      // Calculate remaining prompts before updating state
      const remainingCreated = createdPrompts.filter((p) => p.id !== promptId);

      setCreatedPrompts(remainingCreated);

      // Note: Selection was already switched in handleDeletePrompt when delete started
      // Clean up the ref entry if it exists
      originalSelectionsBeforeDeleteRef.current.delete(promptId);
    } catch (e) {
      console.error("Failed to delete ugc video editing prompt:", e);
      toast.message(
        <ToastContent
          headingPrefix="Failed to delete prompt"
          subheadingPrefix="Please try again."
          isError={true}
        />,
      );
    } finally {
      setPendingDeleteIds((prev) => {
        const next = new Set(prev);
        next.delete(promptId);
        return next;
      });
      clearDeleteTimer(promptId);
    }
  };

  const handleDeletePrompt = (promptId: string) => {
    clearDeleteTimer(promptId);
    const wasSelected = value === promptId;

    // If deleting the selected prompt, switch to first remaining prompt immediately
    if (wasSelected) {
      // Store original selection for undo
      originalSelectionsBeforeDeleteRef.current.set(promptId, value);

      // Calculate remaining prompts (excluding the one being deleted)
      const remainingRecommended = recommendedPrompts.filter((p) => p.id !== promptId);
      const remainingCreated = createdPrompts.filter((p) => p.id !== promptId);
      const firstRemaining =
        remainingRecommended.length > 0
          ? remainingRecommended[0]
          : remainingCreated.length > 0
            ? remainingCreated[0]
            : null;
      onChange(firstRemaining?.id ?? null);
    }

    setPendingDeleteIds((prev) => new Set(prev).add(promptId));
    const t = setTimeout(() => {
      clearDeleteTimer(promptId);
      originalSelectionsBeforeDeleteRef.current.delete(promptId);
      void executeDelete(promptId);
    }, 5000);
    deleteTimersRef.current.set(promptId, t);
  };

  const handleUndoDelete = (promptId: string) => {
    clearDeleteTimer(promptId);

    // Restore original selection if we had switched it
    const originalSelection = originalSelectionsBeforeDeleteRef.current.get(promptId);
    if (originalSelection !== undefined) {
      onChange(originalSelection);
      originalSelectionsBeforeDeleteRef.current.delete(promptId);
    }

    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      next.delete(promptId);
      return next;
    });
  };

  const renderCheckIcon = (isSelected: boolean) => {
    if (!isSelected) return null;
    return (
      <div className="w-[14px] h-[20px] flex items-center justify-center shrink-0">
        <RiCheckLine size={14} className="text-brand-orange shrink-0" />
      </div>
    );
  };

  const PromptItemBase = ({
    promptItem,
    content,
    options = {},
  }: {
    promptItem: UgcVideoEditingPrompt;
    content: React.ReactNode;
    options?: {
      onClick?: () => void;
      className?: string;
      showCheckIcon?: boolean;
      actions?: React.ReactNode;
      showTooltip?: boolean;
    };
  }) => {
    const isSelected = value === promptItem.id;
    const {
      onClick,
      className = "",
      showCheckIcon = true,
      actions,
      showTooltip = true,
    } = options;

    const containerClassName = cn(
      "h-[28px] w-full flex items-center px-[8px] py-0 rounded-[8px]",
      onClick && "group cursor-pointer",
      isSelected
        ? "bg-selected-gray font-medium"
        : onClick
          ? "hover:bg-hover-gray font-normal"
          : "",
      className,
    );

    const itemContent = (
      <div onClick={onClick} className={containerClassName}>
        <div className="w-full flex items-center gap-[6px]">
          {showCheckIcon && renderCheckIcon(isSelected)}
          {content}
          {actions && (
            <div className="hidden group-hover:flex items-center gap-1 shrink-0">
              {actions}
            </div>
          )}
        </div>
      </div>
    );

    if (!showTooltip) return <div key={promptItem.id}>{itemContent}</div>;

    return (
      <div key={promptItem.id}>
        <Tooltip>
          <TooltipTrigger asChild>{itemContent}</TooltipTrigger>
          <TooltipContent side="bottom" align="start" className="max-w-[300px]">
            <p>{promptItem.prompt}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  };

  const renderPromptItem = (promptItem: UgcVideoEditingPrompt) => (
    <PromptItemBase
      promptItem={promptItem}
      content={
        <span className="text-brand-primary-800 text-[12px] leading-[20px] line-clamp-1">
          {promptItem.prompt}
        </span>
      }
      options={{ onClick: () => handleSelectPrompt(promptItem) }}
    />
  );

  const renderCreatedPromptItem = (promptItem: UgcVideoEditingPrompt) => {
    const isPendingDelete = pendingDeleteIds.has(promptItem.id);
    const isEditing = editingPromptId === promptItem.id;

    if (isEditing) {
      return (
        <PromptItemBase
          promptItem={promptItem}
          content={
            <input
              key={promptItem.id}
              ref={editPromptInputRef}
              type="text"
              defaultValue={promptItem.prompt}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  void handleUpdatePrompt();
                } else if (e.key === "Escape") {
                  e.stopPropagation();
                  e.preventDefault();
                  handleCancelEdit();
                }
              }}
              onBlur={() => {
                if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
                const blurredId = promptItem.id;
                blurTimeoutRef.current = setTimeout(() => {
                  if (
                    editingPromptId === blurredId &&
                    document.activeElement !== editPromptInputRef.current
                  ) {
                    handleCancelEdit();
                  }
                  blurTimeoutRef.current = null;
                }, 150);
              }}
              disabled={isUpdatingPrompt}
              className="flex-1 text-brand-primary-800 text-[12px] font-normal leading-[20px] bg-transparent border-none outline-none focus:ring-0 p-0"
              placeholder="Enter prompt..."
            />
          }
          options={{ className: "bg-selected-gray", showTooltip: false }}
        />
      );
    }

    if (isPendingDelete) {
      return (
        <div
          key={promptItem.id}
          className="w-full px-[8px] py-[4px] rounded-[8px] relative bg-hover-gray"
        >
          <div className="flex items-start gap-[6px]">
            <span className="flex-1 text-brand-primary-400 text-[12px] font-normal leading-[20px] line-clamp-1">
              {promptItem.prompt}
            </span>
            <SqButton
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleUndoDelete(promptItem.id);
              }}
              size={"sm"}
              className="h-[24px] flex items-center py-0"
            >
              <RiArrowGoBackLine size={14} className="text-brand-primary-400 shrink-0" />
              <span>Undo</span>
            </SqButton>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-[2px] rounded-full overflow-hidden">
            <div
              key={promptItem.id}
              className="h-full w-full bg-brand-orange origin-left"
              style={{
                transform: "scaleX(0)",
                animation: "deleteProgressScale 5s linear forwards",
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <PromptItemBase
        promptItem={promptItem}
        content={
          <span className="flex-1 text-brand-primary-800 text-[12px] leading-[20px] line-clamp-1">
            {promptItem.prompt}
          </span>
        }
        options={{
          onClick: () => handleSelectPrompt(promptItem),
          actions: (
            <>
              <SqButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditPrompt(promptItem);
                }}
                variant="outline"
                className="h-6 w-6 p-0 bg-white rounded-md"
              >
                <RiEditLine size={14} className="text-brand-primary-400" />
              </SqButton>
              <SqButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePrompt(promptItem.id);
                }}
                className="h-6 w-6 p-0 bg-[#FFF0F0] rounded-md"
                bgColor="#FFCCCC"
                style={{ backgroundColor: "#FFF0F0" }}
              >
                <RiDeleteBinLine size={14} className="text-destructive-700" />
              </SqButton>
            </>
          ),
        }}
      />
    );
  };

  const label = selectedPrompt?.prompt?.trim() ? selectedPrompt.prompt : "Editing prompt";

  return (
    <TooltipProvider>
      <Popover open={open} onOpenChange={handleOpenChange} modal>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className="flex items-center gap-[6px] border-[1px] max-w-[160px]"
          >
            <RiScissorsLine size={16} className="text-brand-primary-400 shrink-0" />
            {isLoadingSelected ? (
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-[12px] w-[72px] rounded-[6px] bg-[#EDEDE7] animate-pulse" />
              </div>
            ) : (
              <span className="text-brand-primary-800 text-[12px] max-w-[92px] truncate">
                {label}
              </span>
            )}
            <LuChevronDown className="h-4 w-4 text-brand-primary-new shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={8}
          className="w-[358px] p-1 shadow-none border-0 squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white drop-shadow-md"
        >
          {/* Search */}
          <div className="mb-[4px]">
            <div className="relative">
              <LuSearch
                size={14}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 text-brand-primary-400"
              />
              <Input
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-7 h-[28px] text-[12px] text-brand-primary-new placeholder:text-brand-primary-400 rounded-[8px] bg-selected-gray flex items-center self-stretch pr-[8px] pt-[8px] pb-[6px] border-none focus:ring-0 shadow-none"
              />
            </div>
          </div>

          <div className="max-h-[300px] overflow-y-auto">
            {isLoadingPrompts ? (
              <div className="flex items-center justify-center py-8">
                <LuLoaderCircle
                  size={20}
                  className="animate-spin text-brand-primary-400"
                />
              </div>
            ) : (
              <>
                {filteredRecommended.length > 0 && (
                  <Collapsible open={recommendedOpen} onOpenChange={setRecommendedOpen}>
                    <CollapsibleTrigger asChild>
                      <div className="group cursor-pointer w-full px-[8px] py-[4px] rounded-[8px] hover:bg-hover-gray">
                        <div className="flex items-center justify-between">
                          <span className="text-brand-primary-400 text-[12px] font-medium leading-[20px]">
                            Recommended
                          </span>
                          <LuChevronDown
                            size={16}
                            className={cn(
                              "text-brand-primary-400 transition-transform duration-200",
                              recommendedOpen ? "" : "-rotate-90",
                            )}
                          />
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      {filteredRecommended.map(renderPromptItem)}
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {(filteredCreated.length > 0 || isAddingNewPrompt) && (
                  <Collapsible open={createdOpen} onOpenChange={setCreatedOpen}>
                    <CollapsibleTrigger asChild>
                      <div className="group cursor-pointer w-full px-[8px] py-[4px] rounded-[8px] hover:bg-hover-gray">
                        <div className="flex items-center justify-between">
                          <span className="text-brand-primary-400 text-[12px] font-medium leading-[20px]">
                            Created by you
                          </span>
                          <LuChevronDown
                            size={16}
                            className={cn(
                              "text-brand-primary-400 transition-transform duration-200",
                              createdOpen ? "" : "-rotate-90",
                            )}
                          />
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      {filteredCreated.map(renderCreatedPromptItem)}
                      {isAddingNewPrompt && (
                        <div className="h-[28px] w-full flex items-center px-[8px] py-[4px]">
                          <div className="w-full flex items-start justify-between gap-2">
                            <input
                              ref={newPromptInputRef}
                              type="text"
                              value={newPromptText}
                              onChange={(e) => setNewPromptText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  void handleCreatePrompt();
                                } else if (e.key === "Escape") {
                                  setIsAddingNewPrompt(false);
                                  setNewPromptText("");
                                }
                              }}
                              onBlur={() => {
                                setTimeout(() => {
                                  if (!newPromptText.trim()) {
                                    setIsAddingNewPrompt(false);
                                    setNewPromptText("");
                                  }
                                }, 150);
                              }}
                              disabled={isCreatingPrompt}
                              className="flex-1 text-brand-primary-800 text-[12px] font-normal leading-[20px] bg-transparent border-none outline-none focus:ring-0 p-0"
                              placeholder="Define your prompt"
                            />
                            {isCreatingPrompt && (
                              <LuLoaderCircle
                                size={14}
                                className="animate-spin text-brand-primary-400 shrink-0"
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {filteredRecommended.length === 0 &&
                  filteredCreated.length === 0 &&
                  searchQuery.trim() && (
                    <div className="px-[8px] py-[12px] text-center text-brand-primary-400 text-[11px]">
                      No results found
                    </div>
                  )}
              </>
            )}
          </div>

          {/* New Prompt Button */}
          <div
            onClick={handleNewPrompt}
            className="h-[28px] cursor-pointer w-full flex items-center px-[8px] py-0 mt-1 rounded-[8px] bg-[#FF45000A] hover:bg-[#FF450014]"
          >
            <div className="flex items-center gap-[6px]">
              <LuPlus size={14} className="text-brand-orange shrink-0" />
              <span className="text-brand-orange text-[12px] font-normal leading-[20px]">
                New Prompt
              </span>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
};

export default UgcVideoEditingPromptDropdown;
